import * as firebase from "firebase";
import { v4 as uuidv4 } from "uuid";
import moment from "moment/moment";
import BusinessHours from 'vue-business-hours';
import GalleryPhotosUploadDlg from "@/views/staff/gallery/GalleryPhotosUploadDlg/GalleryPhotosUploadDlg.vue";
import TimeRangeDropdown from "@/components/pickers/TimeRangeDropdown.vue";

import { NewAdditionalService } from "@/models/additional-service/NewAdditionalService";
import { ValidationRules } from "@/utils/validation-rules";
import { PremiumServiceParticipantsLimit } from "@/enums/PremiumServiceParticipantsLimit";
import { toTranslationKey } from "@/utils/stringFormat";
import { isPremiumServiceAvailableHoursValid, premiumServiceAvailableHours, sortPremiumServiceAvailableHours } from "@/consts/PremiumServiceAvailableHours";
import { slotDurationsInMins } from "@/consts/SlotDurationsInMins";
import { dateFormats } from "@/consts/DateTimeFormats";
import { vueBusinessHoursTranslations } from "@/locales/npms/vue-business-hours/vue-business-hours";
import { convertTimeDurationTo24hFormat } from "@/utils/dateTimeFormat";
import { CreateEvent } from "@/models/calendar/CreateEvent";
import { EventsTypes } from "@/enums/EventsTypes";
import { ConciergeServiceMixin } from "@/mixins/ConciergeService";
import i18n from "@/plugins/vueI18n";
import { optimizePhotos } from "@/utils/upload-image";
import { fileSizeToMB, validFileSize10MB } from "@/utils/fileSizeToMB";
import {EventLocationsMixin} from "@/mixins/EventLocations";
import { EventLocations } from "@/enums/EventLocations";
import { RecurringEventsType, RecurringRepeatType } from "@/enums/RecurringEventsType";
import { waitResizedImage } from "@/utils/wait-resized-image";
import ScheduledPublishEventsDialog from "@/views/calender/view-calendar-item/ScheduledPublishEventsDialog.vue";
import {GalleryImage} from "@/models/GalleryImage";
import { hasAccess } from "@/utils/accessControl/accessControl";
import { AccessMatrixKeys } from "@/utils/accessControl/accessMatrixKeysEnum";
import { UserTypeEnum } from '@/enums/UserTypeEnum';

export default {
  name: "ConciergeServiceCreateDlg",
  mixins: [
    ConciergeServiceMixin,
    EventLocationsMixin
  ],
  components: {
    BusinessHours,
    GalleryPhotosUploadDlg,
    TimeRangeDropdown,
    ScheduledPublishEventsDialog
  },
  props: ["additionalServices", "additionalServiceType"],
  data() {
    return {
      isFormValid: true,
      newService: new NewAdditionalService(),
      dialog: false,
      loading: false,
      availableHours: premiumServiceAvailableHours,
      slotDurationsInMins: slotDurationsInMins,
      isPremiumServiceAvailableHoursValid: true,
      searchCategoryInput: '',
      loadedVendorsData: false,
      oneOffCost: 0,
      menu: false,
      hintDateFormat: '',
      startDateSelected: '',
      minDate: moment(new Date()).format('YYYY-MM-DD'),
      time: {
        start: moment().format(this.$store.state.facilityModule.selectedFacility.timeFormat),
        end: moment().add(1, "h").format(this.$store.state.facilityModule.selectedFacility.timeFormat)
      },
      event: new CreateEvent(),
      indexAdditionalPhoto: null,
      office365DurationMins: 15,
      serviceRequestDurationHours: 1,
      maxSlots: 1,
      participantLimitNumber: 1,
      isOffice365Event: false,
      isLoadedMsCalendarEvents: false,
      isLoadingMeetingTimes: false,
      eventDates: [],
      busyEventDates: [],
      msCalendarEvents: [],
      currentRoomLocation: null,
      saving: false,
      isOpenScheduldEventsDialog: false
    }
  },
  computed: {
    isEvent() {
      return this.additionalServiceType === this.PremiumServiceEventTypes.Event
    },
    participantsLimitItems() {
      const defaultList = [PremiumServiceParticipantsLimit.Unlimited, PremiumServiceParticipantsLimit.SetLimit];
      const timeSlotLimits = [PremiumServiceParticipantsLimit.LimitPerTimeSlot];
      const limits = this.newService.byAppointment ? [...defaultList, ...timeSlotLimits] : defaultList;

      return limits.map(x => ({
        text: toTranslationKey(x),
        value: x
      }));
    },
    label() {
      switch (this.additionalServiceType) {
        case this.PremiumServiceEventTypes.Request:
          return this.$t(`CreatePremiumService.AddRequest`);
        case this.PremiumServiceEventTypes.Event:
          return this.$t(`CreatePremiumService.AddEvent`);
        case this.PremiumServiceEventTypes.Product:
          return this.$t(`CreatePremiumService.AddProduct`);
      }
    },
    title() {
      switch (this.additionalServiceType) {
        case this.PremiumServiceEventTypes.Request:
          return this.$t(`CreatePremiumService.CreateNewRequest`);
        case this.PremiumServiceEventTypes.Event:
          return this.$t(`CreatePremiumService.CreateNewEvent`);
        case this.PremiumServiceEventTypes.Product:
          return this.$t(`CreatePremiumService.CreateNewProduct`);
      }
    },
    localization() {
      return vueBusinessHoursTranslations[this.$i18n.locale];
    },
    rules() {
      return {
        name: [ValidationRules.required, ValidationRules.max(25)],
        provider: [ValidationRules.required],
        categoryId: [ValidationRules.required],
        description: [ValidationRules.required, ValidationRules.max(500)],
        comment: [ValidationRules.max(100)],
        type: [ValidationRules.required],
        participantLimit: [ValidationRules.required],
        serviceType: [ValidationRules.required],
        period: [ValidationRules.required],
        visitorName: [ValidationRules.required],
        selection: [ValidationRules.required],
        slotDurationInMins: [ValidationRules.required],
        oneOffCost: [ValidationRules.required, this.validPrice],
        serviceRequestDurationHours: [ValidationRules.positiveNumber, value => value ? /^\d+(\.\d+)?$/.test(value) || 'Only decimal numbers are allowed' : null],
        maxSlots: [ValidationRules.required, ValidationRules.positiveNumber, value => /^[1-9][0-9]*$/.test(value) || 'Only integers greater than 0 are allowed'],
        participantLimitNumber: [ValidationRules.required, ValidationRules.positiveNumber, value => /^[1-9][0-9]*$/.test(value) || 'Only integers greater than 0 are allowed'],
      };
    },
    validPrice() {
      if(this.newService.oneOffCost === "POA" || this.newService.oneOffCost === "Free") {
        return true;
      }

      return (!isNaN(parseFloat(this.oneOffCost)) && this.oneOffCost >= 0) || i18n.t('ValidationRules.PositiveNumber');
    },
    showAddCategoryBtn() {
      const value = this.searchCategoryInput ? this.searchCategoryInput.trim().toLocaleLowerCase() : ''

      if (value.length <= 0) return false

      const existed = this.serviceCategories.some(x => x.name.trim().toLocaleLowerCase() === value)

      return !existed
    },
    startDateFormatted(){
      if(!this.hintDateFormat || !this.startDateSelected) return ''

      return moment(this.startDateSelected).format(this.hintDateFormat)
    },
    facility() {
      return this.$store.state.facilityModule.selectedFacility;
    },
    isWaitingList() {
      return this.newService.participantLimit === this.PremiumServiceParticipantsLimitTypes.SetLimit;
    },
    serviceCategories() {
      return this.$store.state.socialClubModule.lifestyleProgramCategories
    },
    isEnableRoomBooking() {
      return this.$store.state.facilityModule.selectedFacility.isEnableRoomBooking;
    },
    calendarEvents() {
      return this.$store.state.calenderModule.calenderEvents;
    },
    currentUser(){
        return this.$store.state.authModule.user
    },
    canAddEventLocation(){
        return hasAccess(this.currentUser.accessLevel, AccessMatrixKeys.Calendar_Events_AddLocation)
    }
  },
  async created(): Promise<void> {
    this.$store.dispatch("getFacility");
    this.$store.dispatch("getLifestyleProgramCategories");
    await this.loadVendorsData()
  },
  mounted() {
    const dateFormat = dateFormats.find(x=>x.format == this.selectedFacility.dateFormat)
    this.hintDateFormat = dateFormat ? dateFormat.name : dateFormats[0].name;
    this.startDateSelected = this.minDate;
    this.newService.imageUrl = require("@/assets/no-image.jpg");
    this.newService.thumbnailUrl = require("@/assets/no-image.jpg");
    this.newService.bookByFamily = false;

    if(this.additionalServiceType === this.PremiumServiceEventTypes.Product) {
      this.newService.additionalPhotos = [];
    }
    if(this.additionalServiceType === this.PremiumServiceEventTypes.Event) {
      this.newService.wing = "All";
      this.newService.location = "";
    }
    this.availableHours = premiumServiceAvailableHours;
  },
  methods: {
    async loadVendorsData() {
      if (!this.loadedVendorsData) {
        await this.$store.dispatch("getEmailTemplates");
        await this.$store.dispatch("getVendors");
        this.loadedVendorsData = true
      }
    },
    async addCategory() {
      const value = this.searchCategoryInput?.trim() || ''

      if (!value || value?.length <= 0) return

      if (this.serviceCategories.some(x => x.name === value)) return

      const newCategory = {
        name: value,
        color: '#FFFFFF'
      }

      await this.$store.dispatch('addLifestyleProgramCategory', newCategory)
        .then(async (categoryId) => {
          await this.$store.dispatch("getLifestyleProgramCategories").then(() => {
            this.$forceUpdate();
            this.newService.category = value.name;
            this.newService.categoryId = categoryId;
            this.$toast.open({
              message: this.$t('CreatePremiumService.CategorySuccessfullyAddedMessage') as string,
              type: "success"
            });
          });
        }).catch((error) => {
          console.error(error)
          this.$toast.open({ message: this.$t('GeneralTranslations.UndefinedError') as string, type: "error" });
        });
    },
    decrementDurationMins() {
      if (!this.office365DurationMins || this.office365DurationMins <= 15) {
          this.office365DurationMins = 15;
      }
      else {
          this.office365DurationMins = +this.office365DurationMins-15;
      }

      this.$forceUpdate();
      this.findMeetingTimes();
    },
    incrementDurationMins(): void {

      if (!this.office365DurationMins) {
          this.office365DurationMins = 15;
      }
      else {
          this.office365DurationMins = +this.office365DurationMins+15;
      }
      this.$forceUpdate();
      this.findMeetingTimes();
    },
    async updateLocationValue(value) {

      const locations = this.$store.state.eventLocationModule.eventLocations

      if (locations.some(location => [EventLocations.Location, EventLocations.Room].includes(location.type) && location.name === value) && this.isEnableRoomBooking) {
          this.currentRoomLocation = locations.find(location => location.name === value);

          if(!this.calendarEvents.length){
              await this.$store.dispatch('getCalenderEvents');
          }

          await this.setOffice365EventState(true);
          await this.findMeetingTimes();

          const locationEvents = this.calendarEvents.filter(event => event.location?.length > 0);

          if (locationEvents.length > 0) {
              locationEvents.forEach(event => {
                  const date = new Date(event.startISOString).toISOString().split('T')[0];
                  if(event.location === value){
                      this.eventDates.push(date);
                      this.busyEventDates.push(date);
                  }
              });
          }

         await this.loadMsCalendarEvents()

      } else {
         await this.setOffice365EventState(false)
      }
    },
    async findMeetingTimes(){

      if(!this.isOffice365Event) return

      if(this.newService.isRecurring){

          if(!this.startDateSelected?.length || !this.endDateSelected?.length) {
              this.availableTimes = [{text: this.$t("NewCalendarItem.NotAvailableHours"), value: null}]
              return
          }
          else{
              await this.findMeetingTimesForRecurring()
          }
      }
      else{

          if(!this.startDateSelected?.length ) {
              this.availableTimes = [{text: this.$t("NewCalendarItem.NotAvailableHours"), value: null}]
              return
          }
          else{
              await this.findMeetingTimesForSingleEvent()
          }
      }

    },
    async findMeetingTimesForRecurring(): Promise<void> {

      if(!this.newService.isRecurring || !this.isOffice365Event) return

      this.updateMaxDate()

      this.isLoadingMeetingTimes = true
     
      const startDateTime = moment(this.startDateSelected).set({
          hour: 10,
          minute: 0,
          second: 0
      })

      const endDateTime = moment(this.endDateSelected).set({
          hour: 19,
          minute: 0,
          second: 0
      })

      const params = {
          roomName: this.currentRoomLocation?.isDifferentName ? this.currentRoomLocation.differentName : this.currentRoomLocation.name,
          office365ResourceEmail: this.currentRoomLocation?.office365Email || null,
          startDateTime: startDateTime.format('YYYY-MM-DDTHH:mm:ss'),
          endDateTime: endDateTime.format('YYYY-MM-DDTHH:mm:ss'),
          recurrence: {
              repetitions:this.newService?.recurringRepetitions,
              type: this.newService?.recurringType,
              repeatType: this.newService?.recurringRepeatType,
              repetitionsDays: this.newService?.recurringRepetitionsDays
          },
          meetingDurationInMins: this.office365DurationMins,
          facilityId: this.currentFacility
      }
      this.$store.dispatch('findMsMeetingTimesForRecurring', params)
          .then(response => {
              if (response.data) {
                  const meetingTimeSuggestions = response.data.data.meetingTimeSuggestions

                  if(meetingTimeSuggestions.length > 0){
                      this.availableTimes = meetingTimeSuggestions[0].times.map(time => {
                          return {text: time, value: time}
                      });
                  }
                  else{
                      this.availableTimes = []
                  }
                  
              } else {
                  this.availableTimes = [{text: this.$t("NewCalendarItem.NotAvailableHours"), value: null}];
              }
          }).catch((error) => {
          console.error(error);
      }).finally(() => {
          this.isLoadingMeetingTimes = false
      })
    },
    async findMeetingTimesForSingleEvent(): Promise<void> {

      this.isLoadingMeetingTimes = true

      const startDateTime = moment(this.startDateSelected).set({
          hour: 10,
          minute: 0,
          second: 0
      }).format('YYYY-MM-DDTHH:mm:ss');

      const endDateTime = moment(this.startDateSelected).set({
          hour: 19,
          minute: 0,
          second: 0
      }).format('YYYY-MM-DDTHH:mm:ss');

      if (this.isOffice365Event) {
          const params = {
              roomName: this.currentRoomLocation?.isDifferentName ? this.currentRoomLocation.differentName : this.currentRoomLocation.name,
              office365ResourceEmail: this.currentRoomLocation?.office365Email || null,
              startDateTime,
              endDateTime,
              meetingDurationInMins: this.office365DurationMins,
              facilityId: this.currentFacility
          }
          this.$store.dispatch('findMsMeetingTimes', params)
              .then(response => {
                  if (response.data?.data?.length > 0) {
                      this.availableTimes = response.data.data.map(el => {
                          const timeSlot = `${moment(el.meetingTimeSlot?.start.dateTime).format('hh:mm A')}`;
                          return {text: timeSlot, value: timeSlot}
                      });
                  } else {
                      this.availableTimes = [{text: this.$t("NewCalendarItem.NotAvailableHours"), value: null}];
                  }
              }).catch((error) => {
              console.error(error);
          }).finally(() => {
              this.isLoadingMeetingTimes = false
          })

      }
    },
    async loadMsCalendarEvents(){
      if(this.isOffice365Event) {
          const params = {
              facilityId: this.currentFacility,
              range: {
                  startDateTime: moment().startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
                  endDateTime: moment().endOf('day').add(1, 'month').format('YYYY-MM-DDTHH:mm:ss')
              }
          }

          await this.$store.dispatch('msCalendarView', params).then(response => {
              this.isLoadedMsCalendarEvents = true

              if (response.data?.data?.length > 0) {
                  this.msCalendarEvents = response.data.data
                  this.eventDates = this.eventDates.concat(response.data.data.map(x=>x.date));
              } 
          })
      }
    },
    async setOffice365EventState(state) {
      this.isOffice365Event = state;

      this.updateMaxDate()
    },
    getEventColor(date){
      if (this.busyEventDates.includes(date) || this.msCalendarEvents.some(d => d.date === date && d.items.length > 0)) {
          return'orange';
      } 
      else if (this.msCalendarEvents.some(d => d.date === date && d.items.length <= 0)) {
          return'green';
      }

      return 'white';
    },
    updateMaxDate(){
      if(this.isOffice365Event === true){
          if((this.newService.recurringType === RecurringEventsType.Monthly 
              || (this.newService.recurringType === RecurringEventsType.Custom && 
                  this.newService.recurringRepeatType === RecurringRepeatType.Month))){
              this.maxDate = moment(this.startDateSelected).add(1, "year").format('YYYY-MM-DD'); 
          }
          else{
              this.maxDate = moment(this.startDateSelected).add(50, "days").format('YYYY-MM-DD');
          }
      }
      else{
          this.maxDate = moment(new Date()).add(1, "year").format('YYYY-MM-DD');
      }
    },
    async onChangeSendVendorEmailOption(e) {
      if (e === true) {
        this.newService.sendVendorEmailOptions = {
          vendorId: null,
          vendorName: null,
          emailTemplateId: null
        }
        await this.loadVendorsData()
      }
      else {
        this.newService.sendVendorEmailOptions = null
      }
    },
    onVendorSelected(vendorId) {
      const vendor = this.currentVendors.find(x => x.id == vendorId)
      this.newService.sendVendorEmailOptions.vendorName = vendor.name
    },
    closeDialog(): void {
      this.dialog = false;
      this.newService = new NewAdditionalService();
      this.newService.imageUrl = require("@/assets/no-image.jpg");
      this.newService.thumbnailUrl = require("@/assets/no-image.jpg");
      this.availableHours = premiumServiceAvailableHours
    },
    uploadAdditionalPhoto(index) {
      const refName = `imageUpload${index}`;
      const refElement = this.$refs[refName];

      setTimeout(() => {
        refElement[0].click();
      }, 100)
    },
    async saveProfilePic(e) {
      const ref = uuidv4();
      const file: File | Blob | any = await optimizePhotos(e.target.files[0], '');
      const fileSizeInMegabytes = fileSizeToMB(file?.size);

      if (!validFileSize10MB(fileSizeInMegabytes)) {
        this.$refs.inputUpload.value = null;
        return;
      }

      this.loading = true;

      const storageRef: any = firebase
        .storage()
        .ref()
        .child(this.currentFacility)
        .child("additionalServices")
        .child("images")
        .child(ref)
        .put(file);

        const path = (await storageRef)?.metadata?.fullPath
        const thumbnailImageRef = firebase.storage().ref().child(`${path}_200x200`);

      storageRef.on(
        `state_changed`,
        () => {
          console.log("loading...")
        },
        error => {
          console.error(error.message);
          this.loading = false;
        },
        () => {
          storageRef.snapshot.ref.getDownloadURL().then(async url => {
            this.newService.imageUrl = url;
            this.newService.thumbnailUrl = await waitResizedImage(10, thumbnailImageRef);
            this.$forceUpdate();
            this.loading = false;
          });
        }
      );
    },
    async saveAdditionalPhoto(e, index) {
      const ref = uuidv4();
      const file: File | Blob | any = await optimizePhotos(e.target.files[0], '');
      const fileSizeInMegabytes = fileSizeToMB(file?.size);

      if (!validFileSize10MB(fileSizeInMegabytes)) {
        const refName = `imageUpload${index}`;
        const refElement = this.$refs[refName];

        setTimeout(() => {
          refElement[0].value = null;
        }, 100);
        return;
      }

      const storageRef = firebase
        .storage()
        .ref()
        .child(this.currentFacility)
        .child("additionalServices")
        .child("images")
        .child(ref)
        .put(file);

      storageRef.on(
        `state_changed`,
        () => {
          console.log("loading...")
        },
        error => {
          console.error(error.message);
        },
        () => {
          storageRef.snapshot.ref.getDownloadURL().then(url => {
            this.newService.additionalPhotos[index].photoUrl = url;
            this.$forceUpdate();
          });
        }
      );
    },
    onUpdatedHours(data) {
      const editedPropsNames = Object.keys(data);

      if (!editedPropsNames || editedPropsNames?.length > 1) {
        throw Error('Unhandled error during updating the business-hours')
      }

      const editedDayPropName = editedPropsNames[0]
      const editedDayObj = data[editedDayPropName]
      this.availableHours[editedDayPropName] = editedDayObj
      this.isPremiumServiceAvailableHoursValid = isPremiumServiceAvailableHoursValid(this.availableHours)
    },
    createService(status?: string, scheduledPublishDateTime?: Date): void {
      const form = this.$refs.form as any;
      const validated = form.validate();
      if (!validated || !this.isPremiumServiceAvailableHoursValid) return;

      this.saving = true

      //cast to string for MyHub
      this.newService.availableHours = sortPremiumServiceAvailableHours(this.availableHours);
      this.newService.orderIndex = this.additionalServices.length + 1;

      // for Audit Log
      this.newService.createdById = this.$store.state.authModule.user.id;
      this.newService.createdByType = UserTypeEnum.Staff;
      this.newService.typesOfRecordChanged = this.generateLogAudit(this.additionalServiceType, "Add");

      if(this.newService.isWaitingList) {
        this.newService.waitingResidentsIds = [];
        this.waitingResidents = []
      } else {
        delete this.newService.waitingResidentsIds;
        delete this.newService.waitingResidents;
      }

      if(this.additionalServiceType === this.PremiumServiceEventTypes.Request) {
        this.newService.durationHours = this.serviceRequestDurationHours && this.serviceRequestDurationHours > 0 ?Number(this.serviceRequestDurationHours) : this.serviceRequestDurationHours;
      }

      if(this.isEvent) {
        this.newService.isPublished = status !== this.eventStatus.Draft;

        if(scheduledPublishDateTime) {
          this.newService.scheduledPublishDateTime = scheduledPublishDateTime
        }
      }

      this.newService.participantLimitNumber = Number(this.participantLimitNumber);
      this.newService.maxSlots = Number(this.maxSlots);

      const office365Event = {
        startDateTime: null,
        endDateTime: null
      }

      if(this.additionalServiceType === this.PremiumServiceEventTypes.Event) {
       
        if (this.isOffice365Event) {
  
          const startTime = moment(this.time.start, "hh:mm A")
          const newEndTime = moment(this.time.start, "hh:mm A").add(this.office365DurationMins, 'minutes')

          this.time.end = newEndTime.format("hh:mm A")
  
          office365Event.startDateTime = moment(this.startDateSelected).set({
              hour: startTime.hours(),
              minute: startTime.minutes()
          }).format('YYYY-MM-DDTHH:mm:ss');
          
          office365Event.endDateTime = moment(this.startDateSelected).set({
              hour: newEndTime.hours(),
              minute: newEndTime.minutes()
          }).format('YYYY-MM-DDTHH:mm:ss');

          const start = new Date(`${this.startDateSelected} ${this.time.start}`);
          const end = new Date(`${this.startDateSelected} ${this.time.end}`);
          this.newService.start = firebase.firestore.Timestamp.fromDate(start);
          this.newService.end = firebase.firestore.Timestamp.fromDate(end);
          this.generateEvent();
        }
        else{
          const start = new Date(`${this.startDateSelected} ${this.time.start}`);
          const end = new Date(`${this.startDateSelected} ${this.time.end}`);
          this.newService.start = firebase.firestore.Timestamp.fromDate(start);
          this.newService.end = firebase.firestore.Timestamp.fromDate(end);
          this.generateEvent();
        }
      }

      if(this.newService.categoryId) {
        this.newService.category = this.serviceCategories.find(x => x.id === this.newService.categoryId).name;
      }

      if(this.newService.participantLimit === this.PremiumServiceParticipantsLimitTypes.SetLimit) {
        this.newService.updatedLimitDateTime = firebase.firestore.Timestamp.fromDate(new Date())
      }

      this.$store.dispatch("createAdditionalService", this.newService)
        .then(async (data) => {
          if(this.additionalServiceType === this.PremiumServiceEventTypes.Event) {
            await this.createEvent(data, office365Event);
          }
          this.saving = false;
          this.$router.push(`concierge-service/${data.id}`);
          this.closeDialog();
        }).catch((error) => {
          this.$toast.open({
            message: error.message,
            type: "error",
          })
        })
    },
    generateEvent() {
      this.event.name = this.newService.name;
      this.event.description = this.newService.description;
      this.event.start = this.newService.start;
      this.event.end = this.newService.end;
      this.event.isPublished = true;
      this.event.duration = convertTimeDurationTo24hFormat(`${this.time.start}-${this.time.end}`);
      this.event.type = EventsTypes.PremiumServices;
      this.event.participantLimit = this.newService.participantLimit;
      this.event.participantLimitNumber = this.participantLimitNumber;
      this.event.location = this.newService.location ?? '';
      this.event.wing = this.newService.wing ?? 'All';
      this.event.wingsIds = this.newService.wingsIds ?? [];
      this.event.invitees = []
      this.event.isPublished = this.newService.isPublished ?? false;

      if(this.newService.scheduledPublishDateTime) {
        this.event.scheduledPublishDateTime = this.newService.scheduledPublishDateTime;
      }

      if(this.newService.imageUrl) {
        this.event.imageUrl = this.newService.imageUrl;
        this.event.thumbnailUrl = this.newService.thumbnailUrl;
      }
    },
    async createEvent(data, office365Event) {
      this.event.serviceId = data.id;
      this.event.invitees = [data.id];


     const eventId = await this.$store.dispatch("createNewCalenderEvent", this.event)
     await firebase.firestore().collection('additionalServices').doc(data.id).update({ eventId: eventId });

     if (this.isOffice365Event) {

        const params = {
            subject: this.event.name,
            roomName: this.currentRoomLocation?.isDifferentName ? this.currentRoomLocation.differentName : this.currentRoomLocation.name,
            startDateTime: office365Event.startDateTime,
            endDateTime: office365Event.endDateTime,
            office365ResourceEmail: this.currentRoomLocation?.office365Email || null,
            facilityId: this.currentFacility
        }

         await this.$store.dispatch('createMsEvent', params).then(async response => {
            if(response.data?.status === 200){
                if(response?.data?.data?.id?.length > 0){
                    await firebase.firestore().collection("calender").doc(eventId).update({ office365EventId: response?.data?.data?.id })
                    await firebase.firestore().collection("additionalServices").doc(data.id).update({ office365EventId: response?.data?.data?.id })
                }
            }
            else{
                this.$toast.error(this.$t("NewCalendarItem.ErrorCreatingMsEvent") as string)
            }
        });
      }
    },
    onChangeParticipantLimit() {
      this.participantLimitNumber =
        this.newService.participantLimit === this.PremiumServiceParticipantsLimitTypes.SetLimit ? 1 : null;

      this.maxSlots =
        this.newService.participantLimit === this.PremiumServiceParticipantsLimitTypes.LimitPerTimeSlot ? 1 : null;

      this.$forceUpdate();
    },
    incrementParticipantLimitNumber(): void {
      if (!this.participantLimitNumber) {
        this.participantLimitNumber = 1;
      } else {
        this.participantLimitNumber++;
      }
      this.$forceUpdate();
    },
    decrementParticipantLimitNumber(): void {
      if (this.participantLimitNumber > 1) {
        this.participantLimitNumber--;
        this.$forceUpdate();
      }

      if (!this.participantLimitNumber) {
        this.participantLimitNumber = 1;
        this.$forceUpdate();
      }
    },
    incrementMaxSlots(): void {
      if (!this.maxSlots) {
        this.maxSlots = 1
      } else {
        this.maxSlots++
      }
      this.$forceUpdate();
    },
    decrementMaxSlots(): void {
      if (this.maxSlots > 1) {
        this.maxSlots--;
        this.$forceUpdate();
      }
      if (!this.maxSlots) {
        this.maxSlots = 1
        this.$forceUpdate();
      }
    },
    openGalleryPhotos(index) {
      this.indexAdditionalPhoto = index ?? null;
      (this.$refs.GalleryPhotosUploadDlg as any).open();
    },
    uploadGalleryPhoto(image: GalleryImage) {
      if(this.indexAdditionalPhoto === null) {
        this.newService.imageUrl = image.image;
        this.newService.thumbnailUrl = image?.thumbnailUrl ?? image.image;
      } else {
        this.newService.additionalPhotos[this.indexAdditionalPhoto].photoUrl = image.image;
        this.newService.additionalPhotos[this.indexAdditionalPhoto].thumbnailUrl = image?.thumbnailUrl ?? image.image;
      }
      this.$forceUpdate();
    },
    deleteAdditionalGalleryPhoto(index) {
      this.newService.additionalPhotos.splice(index, 1);
      this.$forceUpdate();
    },
    decrementHours() {
      if (this.serviceRequestDurationHours) {
        let duration = parseFloat(this.serviceRequestDurationHours);
        if (duration > 1) {
          duration -= 1;
          this.serviceRequestDurationHours = duration.toFixed(1);
        } else if (duration === 1) {
          this.serviceRequestDurationHours = null;
        }
        this.$forceUpdate();
      }
    },
    incrementHours() {
      if (!this.serviceRequestDurationHours) {
        this.serviceRequestDurationHours = '1';
      } else {
        let duration = parseFloat(this.serviceRequestDurationHours);
        duration += 1;
        this.serviceRequestDurationHours = duration.toFixed(1);
      }
      this.$forceUpdate();
    },
    onTimeRangeChange(timeRange){
      this.time.start = timeRange.startTime;
      this.time.end = timeRange.endTime;
    },
    addMorePhotos() {
      this.newService?.additionalPhotos.push({
        photoUrl: require("@/assets/no-image.jpg"),
      });
      this.$forceUpdate();
    },
    changeByAppointment() {
      if(this.newService.byAppointment) {
        this.office365DurationMins = null;
      } else {
        this.office365DurationMins = 1;
      }

      this.newService.participantLimit = null;
    },
    handleServiceRequestDurationHours() {
      if(this.serviceRequestDurationHours && (this.serviceRequestDurationHours[0] === "0" || this.serviceRequestDurationHours[0] === "0.0")) {
        this.serviceRequestDurationHours = String(Number(this.serviceRequestDurationHours));
      }
    },
    handleMaxSlots() {
      if(this.maxSlots[0] === "0") {
        this.maxSlots = String(Number(this.maxSlots.replace));
        this.$forceUpdate()
      }
    },
    handleParticipantLimit() {
      if(this.participantLimitNumber[0] === "0") {
        this.participantLimitNumber = String(Number(this.participantLimitNumber));
        this.$forceUpdate()
      }
    },
    handlePrice() {
      if(this.oneOffCost[0] === "0") {
        this.oneOffCost = String(Number(this.oneOffCost));
        this.$forceUpdate()
      }
    },
    openScheduldEventsDialog() {
      this.isOpenScheduldEventsDialog = true
    },
    closeScheduldEventsDialog() {
      this.isOpenScheduldEventsDialog = false
    },
    onScheduldEventsDialogResponse(response) {
      if(response.isScheduled) {
        this.createService(this.eventStatus.Draft, response.scheduledPublishDateTime);
      } else {
        this.createService(this.eventStatus.Draft);
      }
    },
  },
  watch: {
    dialog(newValue) {
      if(newValue) {
        this.newService.type = this.additionalServiceType;

        if(this.newService.type === this.PremiumServiceEventTypes.Request) {
          this.serviceRequestDurationHours = 1;
        }

        if(this.newService.type === this.PremiumServiceEventTypes.Product) {
          this.participantLimitNumber = 1;
        }
      }
    },
    oneOffCost(value) {
      this.newService.oneOffCost = value;
      this.$forceUpdate();
    },
    selectedWingsIds(newValue) {
      this.newService.wingsIds = newValue;
    }
  }
}
